import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import VideoUpload from "./UploadSteps/VideoUpload/VideoUpload";
import MetadataForm from "./UploadSteps/MetadataForm";
import { useSystemSettingsActions } from "@/store";

const initialMetadataDraft = {
  formValues: {
    packageName: "",
    description: "",
    country: "",
    state: "",
    city: "",
    dateShot: undefined,
    notes: "",
    embargo: undefined,
  },
  ccEmails: [],
  isCCEnabled: false,
  isEmbargoEnabled: false,
  isDelegateUpload: false,
  selectedContributor: null,
};

const UploadInputGroup = ({ handleClose }) => {
  const [currentStep, setStep] = useState("videoUpload");
  const [clipsFiles, setClipsFiles] = useState([]);
  const [releasesFiles, setReleasesFiles] = useState([]);
  const [isReleasesOpen, setIsReleasesOpen] = useState(false);
  const [metadataDraft, setMetadataDraft] = useState(initialMetadataDraft);

  const contentRef = useRef(null);

  const { fetchActiveUploader } = useSystemSettingsActions();

  useEffect(() => {
    fetchActiveUploader();
  }, [fetchActiveUploader]);

  // Tutorial safety net: when the "How uploading works" tour ends while the
  // metadata step is NOT mounted (user pressed Back, then left the tour), the
  // practice values would otherwise survive in this draft and re-hydrate on
  // Continue. Scrub them here too — only values that are still the practice
  // text; anything the user typed is left alone.
  useEffect(() => {
    const scrub = () => {
      setMetadataDraft((prev) => {
        const fv = prev?.formValues || {};
        const isPractice = (v, marker) => typeof v === "string" && v.startsWith(marker);
        const nameIsPractice = isPractice(fv.packageName, "PRACTICE —");
        const descIsPractice = isPractice(fv.description, "Practice values from the upload tutorial");
        // Location alone is never treated as practice (US / New York is a real place):
        // it is only scrubbed together with the practice name/description.
        const locIsPractice = (nameIsPractice || descIsPractice) && fv.country === "US" && fv.state === "New York" && fv.city === "New York";
        if (!nameIsPractice && !descIsPractice) return prev;
        return {
          ...prev,
          formValues: {
            ...fv,
            ...(nameIsPractice ? { packageName: "" } : {}),
            ...(descIsPractice ? { description: "" } : {}),
            ...(locIsPractice ? { country: "", state: "", city: "", dateShot: undefined } : {}),
          },
        };
      });
    };
    window.addEventListener("clipn:tutorial-upload-tour-ended", scrub);
    return () => window.removeEventListener("clipn:tutorial-upload-tour-ended", scrub);
  }, []);

  useLayoutEffect(() => {
    const frame = requestAnimationFrame(() => {
      contentRef.current?.scrollIntoView({
        behavior: "instant",
        block: "start",
      });
    });

    return () => cancelAnimationFrame(frame);
  }, [currentStep]);

  const files = useMemo(
      () => [...(clipsFiles || []), ...(releasesFiles || [])],
      [clipsFiles, releasesFiles]
  );

  const renderContent = () => {
    if (currentStep === "videoUpload") {
      return (
          <VideoUpload
              clipsFiles={clipsFiles}
              setClipsFiles={setClipsFiles}
              releasesFiles={releasesFiles}
              setReleasesFiles={setReleasesFiles}
              isReleasesOpen={isReleasesOpen}
              setIsReleasesOpen={setIsReleasesOpen}
              handleNext={() => setStep("metadataForm")}
          />
      );
    }

    if (currentStep === "metadataForm") {
      return (
          <MetadataForm
              files={files}
              metadataDraft={metadataDraft}
              setMetadataDraft={setMetadataDraft}
              handleBack={() => setStep("videoUpload")}
              handleNext={handleClose}
          />
      );
    }

    return null;
  };

  return (
      <div ref={contentRef}>
        {renderContent()}
      </div>
  );
};

export default UploadInputGroup;